import { ComponentPropsWithoutRef, forwardRef } from "react";
import styles from "./Spinner.module.scss";
import cx from "classnames";

/**
 * Props for the Spinner component
 * @extends ComponentPropsWithoutRef<"span">
 */
export type SpinnerProps = ComponentPropsWithoutRef<"span"> & {
  /**
   * When true, the spinner inherits the current text color instead of using the default primary background color
   */
  inherit?: boolean;
};

/**
 * A loading spinner component that displays an animated circular indicator.
 *
 * The Spinner component provides a visual indication that content is loading or processing.
 * It renders as a circular animation that continuously rotates to show activity.
 *
 * Features:
 * - Animated circular loading indicator
 * - Configurable color inheritance
 * - Accessible with proper ARIA attributes
 * - Customizable styling through className and style props
 * - Forwarded ref support
 *
 * @example
 * <Spinner />
 *
 * <Spinner inherit />
 */
export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(
  (props, ref) => {
    const { className, inherit, ...rest } = props;
    const classNames = cx(styles["Spinner"], className, {
      [styles["Inherit"]]: inherit,
    });
    return (
      <span data-anv="spinner" className={classNames} ref={ref}>
        <span className={styles["circle"]} {...rest} />
      </span>
    );
  },
);

Spinner.displayName = "Spinner";
